import { useEffect, useState, type ReactElement } from "react";
import {
  CONTAINMENT_ACTION_STATUSES,
  CountermeasureStatus,
  SOLVE_ACTION_DUE_FILTERS,
  SOLVE_ACTION_GROUPINGS,
  SOLVE_ACTION_OPTIONAL_COLUMNS,
  SOLVE_ACTION_SOURCES,
  SOLVE_ACTION_SOURCE_LABELS,
  SOLVE_ACTIONS_MAX_SELECTED_OWNERS,
  SOLVE_ACTIONS_MAX_SELECTED_SOLVES,
  type SolveActionDueFilter,
  type SolveActionGrouping,
  type SolveActionOptionalColumn,
  type SolveActionSource,
  type SolveActionsInput,
} from "@playbookai/widget-schemas";
import { Checkbox } from "../../atoms/Checkbox/Checkbox.js";
import { RadioGroup } from "../../atoms/RadioGroup/RadioGroup.js";
import { Select } from "../../molecules/Select/Select.js";
import { Accordion } from "../../molecules/Accordion/Accordion.js";
import type { DashboardWidgetEditorProps } from "../types.js";
import { useSolveCatalog } from "../solve-catalog.js";
import { CatalogPickerControls } from "../CatalogPickerControls.js";
import { FilterOptionList } from "../../molecules/FilterMenu/FilterOptionList.js";
import { useAccountOwnerOptions } from "../use-account-owner-options.js";

const DUE_LABELS: Record<SolveActionDueFilter, string> = {
  any: "Any timing",
  overdue: "Overdue",
  next_7_days: "Due in the next 7 days",
  next_30_days: "Due in the next 30 days",
  no_due_date: "No due date",
};

const GROUP_LABELS: Record<SolveActionGrouping, string> = {
  none: "No grouping",
  solve: "By SOLVE",
  source: "By source",
  due: "By due bucket",
};

const COLUMN_LABELS: Record<SolveActionOptionalColumn, string> = {
  priority: "Priority (Containment Plan)",
  impactEffort: "Impact and effort (Countermeasure Table)",
  effectiveness: "Effectiveness (Containment Plan)",
};

const ALL_CONTAINMENT = [...CONTAINMENT_ACTION_STATUSES];
const ALL_COUNTERMEASURE = [...CountermeasureStatus.options];
const NO_IDS: readonly string[] = [];
const NO_OWNERS: readonly string[] = [];

/** Toggle a value in a selection, refusing to leave a `.min(1)` axis empty. */
function toggleRequired<T>(current: readonly T[], value: T): readonly T[] {
  if (!current.includes(value)) return [...current, value];
  if (current.length === 1) return current;
  return current.filter((candidate) => candidate !== value);
}

const LEGEND_CLASS =
  "mb-1 block text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted";
/**
 * SOLVE Actions settings.
 *
 * The draft carries BOTH the widget's presentation params (`groupBy`,
 * `columns`) and its connection parameters (everything else); the host splits
 * them on save, exactly as it does for the KPI pickers and the Kaizen filters.
 *
 * Account selection filters existing assignments; it never grants access.
 * Legacy name filters stay unchanged until an owner mode is explicitly chosen.
 * Due filters apply only to Containment Plan actions.
 */
export function SolveActionsEditor({
  draftParams,
  onChange,
  onValidityChange,
}: DashboardWidgetEditorProps): ReactElement {
  const catalog = useSolveCatalog();
  const draft = draftParams as {
    sources?: readonly SolveActionSource[];
    solveIds?: readonly string[];
    owners?: readonly string[];
    owner?: SolveActionsInput["owner"];
    ownerIds?: readonly string[];
    containmentStatuses?: readonly string[];
    countermeasureStatuses?: readonly string[];
    dueState?: SolveActionDueFilter;
    groupBy?: SolveActionGrouping;
    columns?: readonly SolveActionOptionalColumn[];
  };
  const sources = draft.sources ?? [...SOLVE_ACTION_SOURCES];
  const solveIds = draft.solveIds ?? NO_IDS;
  const owners = draft.owners ?? NO_OWNERS;
  const ownerIds = draft.ownerIds ?? NO_IDS;
  const legacyOwners = draft.owner === undefined && owners.length > 0;
  const owner = draft.owner ?? "any";
  const ownerOptions = useAccountOwnerOptions(ownerIds);
  const containmentStatuses = draft.containmentStatuses ?? ALL_CONTAINMENT;
  const countermeasureStatuses =
    draft.countermeasureStatuses ?? ALL_COUNTERMEASURE;
  const dueState = draft.dueState ?? "any";
  const groupBy = draft.groupBy ?? "solve";
  const columns = draft.columns ?? [];

  const [specificScope, setSpecificScope] = useState(solveIds.length > 0);
  useEffect(() => {
    onValidityChange?.(
      specificScope && solveIds.length === 0
        ? "Choose at least one SOLVE, or use All accessible."
        : owner === "selected" && ownerIds.length === 0
          ? "Choose at least one owner, or use Anyone."
          : null,
    );
  }, [
    onValidityChange,
    specificScope,
    solveIds.length,
    owner,
    ownerIds.length,
  ]);
  const activate = catalog?.activate;
  useEffect(() => {
    if (specificScope) activate?.(solveIds);
  }, [activate, specificScope, solveIds]);

  const commit = (patch: Record<string, unknown>): void =>
    onChange({
      ...draftParams,
      sources,
      solveIds,
      owners,
      ...(!legacyOwners ? { owner, ownerIds } : {}),
      containmentStatuses,
      countermeasureStatuses,
      dueState,
      groupBy,
      columns,
      ...patch,
    });

  const containmentSelected = sources.includes("containment");
  const countermeasureSelected = sources.includes("countermeasure");

  return (
    <div className="space-y-4">
      <fieldset className="space-y-3">
        <legend className="mb-2 text-sm font-semibold text-pb-text">
          SOLVEs
        </legend>
        <RadioGroup
          aria-label="SOLVE scope"
          className="grid grid-cols-2"
          value={specificScope ? "specific" : "all"}
          options={[
            { value: "all", label: "All accessible" },
            { value: "specific", label: "Choose specific" },
          ]}
          onChange={(mode) => {
            setSpecificScope(mode === "specific");
            if (mode === "all") commit({ solveIds: [] });
          }}
        />
        {specificScope ? (
          <CatalogPickerControls
            label="SOLVEs"
            requireSearch
            query={catalog?.searchQuery}
            isLoading={catalog?.isLoading}
            isLoadingMore={catalog?.isLoadingMore}
            error={catalog ? catalog.error : "SOLVE catalogue unavailable."}
            hasMore={catalog?.hasMore}
            isEmpty={!catalog?.solves.length}
            onSearchChange={catalog?.onSearchChange}
            onLoadMore={catalog?.onLoadMore}
            onRetry={catalog?.onRetry}
            items={(catalog?.solves ?? []).map((solve) => ({
              id: solve.id,
              label: solve.title,
            }))}
            selectedIds={solveIds}
            maxSelected={SOLVE_ACTIONS_MAX_SELECTED_SOLVES}
            onSelectionChange={(ids) => commit({ solveIds: ids })}
          />
        ) : (
          <p className="text-dense-1 text-pb-text-muted">
            Includes actions from every SOLVE you can access.
          </p>
        )}
      </fieldset>
      <fieldset className="space-y-3">
        <legend className="mb-2 text-sm font-semibold text-pb-text">
          Action owners
        </legend>
        <RadioGroup
          aria-label="Action owners"
          className="grid grid-cols-2"
          value={legacyOwners ? "legacy" : owner}
          options={[
            { value: "any", label: "Anyone" },
            { value: "me", label: "Me" },
            {
              value: "selected",
              label: "Specific people",
              ...(!ownerOptions.available
                ? { disabledReason: "People search is unavailable here." }
                : {}),
            },
            { value: "unassigned", label: "Unassigned" },
            ...(legacyOwners
              ? [{ value: "legacy", label: "Saved owner names" }]
              : []),
          ]}
          onChange={(mode) => {
            if (mode === "legacy") return;
            commit({
              owner: mode,
              owners: [],
              ownerIds: mode === "selected" ? ownerIds : [],
            });
          }}
        />
        {legacyOwners && (
          <p className="text-dense-1 text-pb-text-muted">
            Saved name filter: {owners.join(", ")}. Choose an owner option above
            to replace this filter with account selection.
          </p>
        )}
        {owner === "selected" && (
          <FilterOptionList
            source={ownerOptions.source}
            label="owners"
            multiple
            values={ownerIds}
            maxSelected={SOLVE_ACTIONS_MAX_SELECTED_OWNERS}
            selectedLabel={ownerOptions.selectedLabel}
            onChange={(ids) => commit({ owners: [], ownerIds: [...ids] })}
          />
        )}
        {owner === "me" && (
          <p className="text-dense-1 text-pb-text-muted">
            Actions assigned to your account. Saved owner names without an
            account link won’t match.
          </p>
        )}
        {owner === "unassigned" && (
          <p className="text-dense-1 text-pb-text-muted">
            Actions without a linked owner, including saved name-only owners.
          </p>
        )}
      </fieldset>
      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-semibold text-pb-text">
          Action sources
        </legend>
        {SOLVE_ACTION_SOURCES.map((source) => (
          <Checkbox
            key={source}
            className="flex"
            label={SOLVE_ACTION_SOURCE_LABELS[source]}
            checked={sources.includes(source)}
            disabled={sources.includes(source) && sources.length === 1}
            onChange={() =>
              commit({ sources: toggleRequired(sources, source) })
            }
          />
        ))}
      </fieldset>
      <Select
        label="Due date"
        value={dueState}
        options={SOLVE_ACTION_DUE_FILTERS.map((value) => ({
          value,
          label: DUE_LABELS[value],
        }))}
        onChange={(value) => commit({ dueState: value })}
        hint="Containment Plan only. Due-date filters exclude countermeasures because their implementation windows are free text."
      />
      <Accordion
        items={[
          {
            id: "details",
            title: "Status and display options",
            content: (
              <div className="space-y-4">
                {containmentSelected && (
                  <fieldset className="space-y-2">
                    <legend className={LEGEND_CLASS}>
                      Containment Plan status
                    </legend>
                    <div className="grid grid-cols-2 gap-3">
                      {CONTAINMENT_ACTION_STATUSES.map((status) => (
                        <Checkbox
                          key={status}
                          label={status}
                          checked={containmentStatuses.includes(status)}
                          disabled={
                            containmentStatuses.includes(status) &&
                            containmentStatuses.length === 1
                          }
                          onChange={() =>
                            commit({
                              containmentStatuses: toggleRequired(
                                containmentStatuses,
                                status,
                              ),
                            })
                          }
                        />
                      ))}
                    </div>
                  </fieldset>
                )}
                {countermeasureSelected && (
                  <fieldset className="space-y-2">
                    <legend className={LEGEND_CLASS}>
                      Countermeasure Table status
                    </legend>
                    <div className="grid grid-cols-2 gap-3">
                      {ALL_COUNTERMEASURE.map((status) => (
                        <Checkbox
                          key={status}
                          label={status}
                          checked={countermeasureStatuses.includes(status)}
                          disabled={
                            countermeasureStatuses.includes(status) &&
                            countermeasureStatuses.length === 1
                          }
                          onChange={() =>
                            commit({
                              countermeasureStatuses: toggleRequired(
                                countermeasureStatuses,
                                status,
                              ),
                            })
                          }
                        />
                      ))}
                    </div>
                  </fieldset>
                )}
                <Select
                  label="Grouping"
                  value={groupBy}
                  options={SOLVE_ACTION_GROUPINGS.map((value) => ({
                    value,
                    label: GROUP_LABELS[value],
                  }))}
                  onChange={(value) => commit({ groupBy: value })}
                />
                <fieldset className="space-y-2">
                  <legend className={LEGEND_CLASS}>Extra details</legend>
                  {SOLVE_ACTION_OPTIONAL_COLUMNS.map((column) => (
                    <Checkbox
                      key={column}
                      className="flex"
                      label={COLUMN_LABELS[column]}
                      checked={columns.includes(column)}
                      onChange={() =>
                        commit({
                          columns: columns.includes(column)
                            ? columns.filter((value) => value !== column)
                            : [...columns, column],
                        })
                      }
                    />
                  ))}
                </fieldset>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
